// BUG-534 — module-scoped throttle for the lazy refetch in
// `Chat.hook.resetFunnelInfo`'s cache-hit branch. Replaces Daniel's
// per-hook-lifecycle `useRef<Set<string>>` from PR #2425. The throttle has to
// be shared between consumers AND the cache-invalidating provider so a fresh
// `funnel_event.updated` can reset a funnel's "already checked" state, forcing
// the next `resetFunnelInfo` call to re-fetch users[].
//
// Usage:
//
//   - Chat.hook.resetFunnelInfo (consumer): on cache hit, calls
//     `wasFunnelChecked(funnelId)` to decide whether to fire the BG fetch,
//     and `markFunnelChecked(funnelId)` immediately before dispatching.
//   - FunnelCache.provider (producer): on every accepted MERGE, calls
//     `clearFunnelChecked(updatedUuid)` so the next consumer read forces a
//     refetch. This is the round-2-A1 reset semantic the plan describes.
//
// Semantic shift from Daniel's PR #2425: the ref was `useRef<Set<string>>`
// (per-hook-lifecycle, reset on remount). This module is session-scoped
// (reset on full page reload). For Daniel's original "user legitimately
// lacks can_view_handle" use case, session-scope is strictly better —
// fewer wasted fetches. For BUG-534's "users[] invalidated by upstream
// event" use case, the provider's `clearFunnelChecked` re-opens the
// throttle exactly when it should re-open.

const staleFunnelsChecked = new Set<string>();

export function markFunnelChecked(uuid: string): void {
  staleFunnelsChecked.add(uuid);
}

export function wasFunnelChecked(uuid: string): boolean {
  return staleFunnelsChecked.has(uuid);
}

export function clearFunnelChecked(uuid: string): void {
  staleFunnelsChecked.delete(uuid);
}

// Test-only seam: per-test reset for vitest isolation. The Set is module-
// scoped so vitest's `clearAllMocks()` does not touch it (same pattern as
// `recentlyEvictedFunnels.clearAll*`).
export function clearAllStaleFunnelsChecked(): void {
  staleFunnelsChecked.clear();
}
